import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import conversionService from '../services/conversionService';
import { toast, Toaster } from 'react-hot-toast';
import EditConversionModal from '../components/EditConversionModal';

const ConversionDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const loadConversion = useCallback(async () => {
    try {
      const data = await conversionService.getConversionById(id);
      setConversion(data);
    } catch (err) {
      console.error('Error al cargar la conversión:', err);
      toast.error('Error al cargar los detalles de la conversión');
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await loadConversion();
      setLoading(false);
    };
    fetchData();
  }, [id, loadConversion]);

  const handleRetry = async () => {
    try {
      setProcessing(true);
      await conversionService.retryConversion(id);
      toast.success('Conversión reenviada correctamente');
      const refreshData = async () => {
        setLoading(true);
        await loadConversion();
        setLoading(false);
      };
      refreshData();
    } catch (err) {
      console.error('Error al reintentar la conversión:', err);
      toast.error('Error al reintentar la conversión');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatConversionDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit', 
      timeZone: 'Europe/Madrid'
    });
  };

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleConversionUpdated = () => {
    handleCloseEditModal();
    const refreshData = async () => {
      setLoading(true);
      await loadConversion();
      setLoading(false);
    };
    refreshData();
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (!conversion) {
    return (
      <div className="space-y-4">
        <Link to="/conversions" className="btn btn-ghost">
          ← Volver a Conversiones
        </Link>
        <div className="alert alert-info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          <span>No se encontró la conversión con ID: {id}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Toaster position="top-right" />
      {conversion && (
        <EditConversionModal 
          conversion={conversion}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onConversionUpdated={handleConversionUpdated}
        />
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <Link to="/conversions" className="btn btn-ghost">
          ← Volver a Conversiones
        </Link>
        <div className="flex flex-wrap gap-2">
          {conversion.conversionError && !conversion.conversionReported && (
            <button 
              className="btn btn-error btn-sm"
              onClick={handleRetry}
              disabled={processing}
            >
              {processing ? (
                <><span className="loading loading-spinner loading-xs"></span> Reintentando...</>
              ) : (
                'Reintentar Envío a Google Ads'
              )}
            </button>
          )}
          <button 
            className="btn btn-outline btn-primary btn-sm"
            onClick={handleOpenEditModal}
          >
            Editar
          </button>
        </div>
      </div>

      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Detalles de la Conversión</h2>
          <div className="divider"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-base-content/70">ID</h3>
                <p className="text-base-content">{conversion.id}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-base-content/70">ID de Venta</h3>
                <Link 
                  to={`/sales/${conversion.saleId}`}
                  className="text-base-content link link-hover"
                >
                  {conversion.saleId}
                </Link>
              </div>

              <div>
                <h3 className="text-sm font-medium text-base-content/70">GCLID</h3>
                <p className="text-base-content break-all">{conversion.gclid || '-'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-base-content/70">Valor de Conversión</h3>
                <p className="text-base-content">{conversion.conversionValue !== undefined ? `${conversion.conversionValue} ${conversion.currency_code || 'EUR'}` : '-'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-base-content/70">Fecha de Conversión (para Google Ads)</h3>
                <p className="text-base-content">{formatConversionDateTime(conversion.conversion_event_time)}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-base-content/70">Fecha de Creación (Registro)</h3>
                <p className="text-base-content">{formatDate(conversion.createdAt)}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-base-content/70">Última Edición (Registro)</h3>
                <p className="text-base-content">{formatDate(conversion.updatedAt)}</p>
              </div>
            </div>

            <div className="space-y-4">


              {conversion.Sale && (
                <>
                  <div>
                    <h3 className="text-sm font-medium text-base-content/70">Estado de la Venta</h3>
                    <span className={`badge ${
                      conversion.Sale.status === 'approved' ? 'badge-success' :
                      conversion.Sale.status === 'rejected' ? 'badge-error' :
                      'badge-warning'
                    }`}>
                      {conversion.Sale.status === 'approved' ? 'Aprobada' :
                       conversion.Sale.status === 'rejected' ? 'Rechazada' :
                       'Pendiente'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-base-content/70">Fecha de Venta</h3>
                    <p className="text-base-content">{formatDate(conversion.Sale.saleDate)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-base-content/70">Última Edición</h3>
                    <p className="text-base-content">{formatDate(conversion.Sale.updatedAt)}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionDetailScreen; 
